/**
 * MembershipManager — Phase 11: per-neighborhood Semaphore membership groups.
 *
 * Each neighborhood maintains a Merkle group of member commitments. When a
 * new peer redeems an invite (Phase 6 flow), their commitment is added to
 * the group. Callers later prove "I'm in this group" without revealing
 * which commitment is theirs.
 *
 * v1 of Phase 11 wires the storage + audit + proof helpers behind a
 * pluggable `MembershipProver` (Plaintext for tests, Semaphore for prod).
 * Cross-device group sync (a `mesh-membership-{neighborhoodId}` OrbitDB log)
 * is Phase 11.5 — for now each device maintains its own view, and an invite
 * round-trip adds commitments on both sides explicitly.
 */

import type { AppContext } from '../app';
import type {
  AuditLogRecord,
  MembershipLogRecord,
} from '../storage/schema';
import {
  PlaintextMembershipProver,
  type MembershipIdentity,
  type MembershipProof,
  type MembershipProver,
  type VerifyProofResult,
} from './MembershipProver';

// ─────────────────────────────────────────────────────────────────────────────
// MANAGER
// ─────────────────────────────────────────────────────────────────────────────

export class MembershipManager {
  readonly #ctx: AppContext;
  readonly #prover: MembershipProver;

  constructor(ctx: AppContext, prover?: MembershipProver) {
    this.#ctx = ctx;
    this.#prover = prover ?? new PlaintextMembershipProver(ctx.identity);
  }

  get prover(): MembershipProver { return this.#prover; }

  /**
   * Derive the local device's stable membership identity. The secret is a
   * keyring-derived sub-key under the 'hub:membership' context; same seed
   * → same commitment across sessions.
   */
  async ownIdentity(): Promise<MembershipIdentity> {
    const seedBytes = await this.#ctx.identity.pubkey({
      environment: 'hub', purpose: 'membership',
    });
    // Pubkey alone isn't secret enough — domain-separate it with a salt and
    // a hash so the application can't conflate this with the pubkey itself.
    const { blake3 } = await import('@noble/hashes/blake3');
    const secret = blake3(
      new Uint8Array([...new TextEncoder().encode('alds-membership-v1:'), ...seedBytes]),
    );
    return this.#prover.createIdentity(secret);
  }

  /** Add a commitment to a neighborhood's local group. Idempotent. Audited. */
  async addMember(neighborhoodId: string, commitment: string, addEnvelopeHash: string = ''): Promise<MembershipLogRecord> {
    const db = this.#ctx.storage.db;
    const id = `${neighborhoodId}:${commitment}`;
    const existing = await db.membership_log.get(id);
    const now = new Date().toISOString();
    const record: MembershipLogRecord = {
      id,
      neighborhoodId,
      commitment,
      addedAt: existing?.addedAt ?? now,
      addEnvelopeHash: existing?.addEnvelopeHash ?? addEnvelopeHash,
    };
    await db.membership_log.put(record);
    if (existing === undefined) {
      await this.#audit({
        action: 'membership.added',
        context: `hub:membership:${neighborhoodId}`,
        details: { neighborhoodId, commitmentPrefix: commitment.slice(0, 12) },
      });
    }
    return record;
  }

  async listMembers(neighborhoodId: string): Promise<MembershipLogRecord[]> {
    const rows = await this.#ctx.storage.db.membership_log
      .where('neighborhoodId').equals(neighborhoodId).toArray();
    rows.sort((a, b) => a.addedAt.localeCompare(b.addedAt));
    return rows;
  }

  /** Whether a commitment is in the local view of a neighborhood's group. */
  async isMember(neighborhoodId: string, commitment: string): Promise<boolean> {
    const row = await this.#ctx.storage.db.membership_log.get(`${neighborhoodId}:${commitment}`);
    return row !== undefined;
  }

  /** Compute the Merkle root of a neighborhood's group, in the prover's format. */
  async getMerkleRoot(neighborhoodId: string): Promise<string> {
    const members = await this.listMembers(neighborhoodId);
    return this.#prover.computeRoot(members.map((m) => m.commitment));
  }

  /**
   * Generate a membership proof for an outgoing incident. Caller provides
   * the external nullifier (typically the incidentId) and the message
   * binding (e.g., the incident's category + severity, hashed by the
   * prover). Throws if our identity isn't in the group.
   */
  async generateProofForIncident(opts: {
    neighborhoodId: string;
    externalNullifier: string;
    message: string;
  }): Promise<MembershipProof> {
    const identity = await this.ownIdentity();
    const inGroup = await this.isMember(opts.neighborhoodId, identity.commitment);
    if (!inGroup) {
      throw new Error(
        `MembershipManager.generateProofForIncident: own commitment ${identity.commitment.slice(0, 12)} not in group ${opts.neighborhoodId}`,
      );
    }
    const members = await this.listMembers(opts.neighborhoodId);
    const proof = await this.#prover.generateProof({
      identity,
      group: members.map((m) => m.commitment),
      externalNullifier: opts.externalNullifier,
      message: opts.message,
    });
    await this.#audit({
      action: 'membership.proof-attached',
      context: `hub:membership:${opts.neighborhoodId}`,
      details: {
        neighborhoodId: opts.neighborhoodId,
        nullifierPrefix: proof.nullifier.slice(0, 12),
        impl: proof.impl,
      },
    });
    return proof;
  }

  /**
   * Verify an inbound proof against the local view of the group. Returns
   * the verification result + whether the nullifier has been seen before
   * (caller checks this for replay protection at their layer).
   */
  async verifyProofAgainst(opts: {
    neighborhoodId: string;
    proof: MembershipProof;
    externalNullifier: string;
    message: string;
  }): Promise<VerifyProofResult> {
    const expectedRoot = await this.getMerkleRoot(opts.neighborhoodId);
    const result = await this.#prover.verifyProof({
      proof: opts.proof,
      expectedRoot,
      externalNullifier: opts.externalNullifier,
      message: opts.message,
    });
    await this.#audit({
      action: 'membership.proof-verified',
      context: `hub:membership:${opts.neighborhoodId}`,
      details: {
        neighborhoodId: opts.neighborhoodId,
        ok: result.ok,
        ...(result.reason !== undefined ? { reason: result.reason } : {}),
        nullifierPrefix: opts.proof.nullifier.slice(0, 12),
        impl: opts.proof.impl,
      },
    });
    return result;
  }

  // ─── internals ──────────────────────────────────────────────────────────

  async #audit(input: { action: string; context: string; details: Record<string, unknown> }): Promise<void> {
    const occurredAt = new Date().toISOString();
    const canonical = JSON.stringify({
      category: 'membership' as const,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
    });
    const signature = await this.#ctx.identity.sign(
      new TextEncoder().encode(canonical),
      { environment: 'hub', purpose: 'membership' },
    );
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: 'membership',
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
      signature,
    };
    try { await this.#ctx.storage.db.audit_log.add(record); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`MembershipManager.audit: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
}
